import { useMemo } from 'react'

import { faTag } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, ButtonProps, Tooltip } from '@mui/material'
import { numToCurrency } from '@utils/stringHelpers'
import BlanketMarkup from '@views/MarketBaskets/BlanketMarkup/BlanketMarkup'
import { useMarketBasketContext } from '@views/MarketBaskets/MarketBaskets.hooks'
import { BooleanParam, useQueryParam } from 'use-query-params'

type Props = ButtonProps & {
	iconOnly?: boolean
	disabled?: boolean
}

export default function BlanketMarkupButton({
	iconOnly = false,
	disabled = false,
	...rest
}: Props) {
	const [drawerVisible, setDrawerVisible] = useQueryParam('blanket-markup', BooleanParam)
	const { marketBasket } = useMarketBasketContext()

	if (!marketBasket) return null

	const { blanketMarkupAmount, blanketMarkupType } = marketBasket

	return (
		<>
			{drawerVisible && <BlanketMarkup />}

			<BlanketMarkupButtonUI
				markupType={blanketMarkupType}
				markup={blanketMarkupAmount}
				currency={marketBasket.currency}
				iconOnly={iconOnly}
				loading={disabled}
				onClick={() => setDrawerVisible(true)}
				{...rest}
			/>
		</>
	)
}

type PropsUi = ButtonProps & {
	loading?: boolean
	markup: number | undefined
	markupType: string | undefined
	iconOnly: boolean
	currency: string | undefined
}

export function BlanketMarkupButtonUI({
	loading = false,
	markup,
	markupType,
	iconOnly,
	currency,
	onClick,
	...rest
}: PropsUi) {
	const markupLabel = useMemo(() => {
		const base = 'Markup'

		if (!markupType) return base

		if (markupType === 'FEE_PER_CASE') {
			const formattedMarkup = numToCurrency(markup, currency)
			return `${base} ${formattedMarkup}`
		}

		return `${base} ${markup}%`
	}, [currency, markup, markupType])

	const buttonColor = markup ? 'success' : 'primary'

	return (
		<Tooltip title={markupLabel} placement="bottom">
			<span>
				<Button
					loading={loading}
					loadingPosition="start"
					onClick={onClick}
					startIcon={<FontAwesomeIcon icon={faTag} />}
					variant={markup ? 'contained' : 'outlined'}
					color={buttonColor}
					data-testid="blanket-markup-button"
					sx={
						iconOnly
							? {
									minWidth: 'auto', // Removes the default wide button layout
									padding: '8px', // Centers and cushions the icon evenly
									'& .MuiButton-startIcon': {
										margin: 0, // Removes the right margin from the span
									},
								}
							: undefined
					}
					{...rest}
				>
					{iconOnly ? null : markupLabel}
				</Button>
			</span>
		</Tooltip>
	)
}
